import React, { useMemo, useState } from "react";
import { useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { addDays } from "date-fns";
import { pageSizeType, retrieveNewsArticlesSearch, sortByType, }
  from "../../../../redux/slice/news-articles/news-articles-search-slice";
import { RootState, useAppDispatch } from "../../../../redux/store";
import { useSelector } from "react-redux";
import Pagination, {
  bootstrap5PaginationPreset,
} from "react-responsive-pagination";
import LoadingComp from "../../../../components/re-usable/loading/loading";
import { retrieveNewsArticlesSearchCategory } from "../../../../redux/slice/news-articles/news-articles-search-category-slice";
import { retrieveNewsArticlesSearchAuthor } from "../../../../redux/slice/news-articles/news-articles-search-author-slice";
import { useLocation, useNavigate, useParams, Location } from "react-router-dom";
import { Link } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import LoadNewsAndArticlesListingAds from "../../../../components/advertisement/load-news-and-articles-listing-ads";

type FilterValues = {
  urlPageSize: number;
  urlSortBy: string;
  urlPageNumber: number;
  urlKeyword: string;
  urlCategoryID: number;
  urlAuthorCode: string;
  urlFromDate: string;
  urlToDate: string;
  urlDocumentTags: string;
};

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  return year + month + day;
};

const formatUrlDate = (date: string) => {
  if (date) {
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);
    return year && month && day && `${month}/${day}/${year}`;
  }
};

const NewsAndArticleListComponent = (): JSX.Element => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const navigate = useNavigate();
  const location: Location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const getUrlFromDate =
    queryParams && formatUrlDate(queryParams.get("fromdate")!);
  const getUrlToDate = queryParams && formatUrlDate(queryParams.get("todate")!);
  const defaultFilterValues = {
    urlPageSize: Number(queryParams.get("pagesize")) || 12,
    urlSortBy: queryParams.get("sortby") || "Date",
    urlPageNumber: Number(queryParams.get("page")) || 1,
    urlKeyword: queryParams.get("searchtext") || "",
    urlCategoryID: Number(queryParams.get("category")) || 0,
    urlAuthorCode: queryParams.get("author") || "",
    urlFromDate: queryParams.get("fromdate") || "",
    urlToDate: queryParams.get("todate") || "",
    urlDocumentTags: queryParams.get("DocumentTags") || "",
  };

  const [filterValues, setFilterValues] =
    useState<FilterValues>(defaultFilterValues);

  const [pageSize, setPageSize] = useState<pageSizeType>(
    (Number(queryParams.get("pagesize")) as pageSizeType) || 12
  );
  const [sortBy, setsortBy] = useState<sortByType>(
    (queryParams.get("sortby") as sortByType) || "Date"
  );
  const [pageNumber, setPageNumber] = useState<number>(
    Number(queryParams.get("page")) || 1
  );
  const [keyword, setKeyword] = useState<string>(
    queryParams.get("searchtext") || ""
  );
  const [isKeyword, setIsKeyword] = useState<boolean>(false);
  const [categoryID, setCategoryID] = useState<number>(
    Number(queryParams.get("category")) || 0
  );
  const [authorCode, setAuthorCode] = useState<string>(
    queryParams.get("author") || ""
  );
  const [fromDate, setFromDate] = useState<string>(getUrlFromDate || "");
  const [toDate, setToDate] = useState<string>(getUrlToDate || "");
  const [documentTags, setDocumentTags] = useState<string>(
    queryParams.get("DocumentTags") || ""
  );
  const [isShowFilterDate, setIsShowFilterDate] = useState<boolean>(false);
  const [isShowFilter, setIsShowFilter] = useState<boolean>(true);

  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);

  const { NewsArticlesSearcData, NewsArticlesSearcLoading } = useSelector(
    (state: RootState) => state.newsArticlesSearch
  );
  const { NewsArticlesSearchCategoryData } = useSelector(
    (state: RootState) => state.newsArticlesSearchCategory
  );
  const { NewsArticlesSearchAuthorData } = useSelector(
    (state: RootState) => state.newsArticlesSearchAuthor
  );

  let NewsArticlesSearcType = {
    pageSize: pageSize,
    sortBy: sortBy,
    pageNumber: pageNumber,
    keyword: keyword,
    categoryID: categoryID,
    authorCode: authorCode,
    fromDate: fromDate,
    toDate: toDate,
    documentTags: documentTags,
  };

  useEffect(() => {
    dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
    dispatch(retrieveNewsArticlesSearchCategory());
    dispatch(retrieveNewsArticlesSearchAuthor());
  }, []);

  useEffect(() => {
    // Any clicks that happens in this page calls the api, so each change can be safely reset when user presses the back button.
    if (defaultFilterValues.urlAuthorCode !== authorCode) {
      setAuthorCode(defaultFilterValues.urlAuthorCode);
    }
    if (defaultFilterValues.urlCategoryID !== categoryID) {
      setCategoryID(defaultFilterValues.urlCategoryID);
    }
    if (defaultFilterValues.urlDocumentTags !== documentTags) {
      setCategoryID(defaultFilterValues.urlCategoryID);
    }
    if (defaultFilterValues.urlFromDate !== fromDate) {
      setFromDate(formatUrlDate(defaultFilterValues.urlFromDate)!);
    }
    if (defaultFilterValues.urlKeyword !== keyword) {
      setKeyword(defaultFilterValues.urlKeyword);
    }
    if (defaultFilterValues.urlPageNumber !== pageNumber) {
      setPageNumber(defaultFilterValues.urlPageNumber);
    }
    if (defaultFilterValues.urlPageSize !== pageSize) {
      setPageSize(defaultFilterValues.urlPageSize as pageSizeType);
    }
    if (defaultFilterValues.urlSortBy !== sortBy) {
      setsortBy(defaultFilterValues.urlSortBy as sortByType);
    }
    if (defaultFilterValues.urlToDate !== toDate) {
      setToDate(formatUrlDate(defaultFilterValues.urlToDate)!);
    }
  }, [location]);

  const updateUrl = (
    sortBy?: string,
    pageSize?: number,
    urlPageNumber?: number,
    urlKeyword?: string,
    urlCategoryID?: number,
    urlAuthorCode?: string,
    urlFromDate?: string,
    urlToDate?: string,
    urlDocumentTags?: string
  ) => {
    const urlSearchParams = new URLSearchParams();
    sortBy && urlSearchParams.append("sortby", sortBy);
    pageSize && urlSearchParams.append("pagesize", pageSize.toString());
    urlPageNumber && urlSearchParams.append("page", urlPageNumber.toString());
    urlKeyword && urlSearchParams.append("searchtext", urlKeyword.toString());
    urlCategoryID &&
      urlSearchParams.append("category", urlCategoryID.toString());
    urlAuthorCode && urlSearchParams.append("author", urlAuthorCode.toString());
    urlFromDate && urlSearchParams.append("fromdate", urlFromDate);
    urlToDate && urlSearchParams.append("todate", urlToDate);
    urlDocumentTags && urlSearchParams.append("DocumentTags", urlDocumentTags);
    navigate(`/news-and-articles/news-articles?${urlSearchParams.toString()}`);
  };

  useEffect(() => {
    if (getUrlFromDate) {
      setFromDate(getUrlFromDate);
      const newFromDate = new Date(getUrlFromDate);
      setStartDate(newFromDate);
    }

    if (getUrlToDate) {
      setToDate(getUrlToDate);
      const newToDate = new Date(getUrlToDate);
      setEndDate(newToDate);
    }
  }, [getUrlFromDate, getUrlToDate]);

  const clearSort = () => {
    setKeyword("");
    setCategoryID(0);
    setFromDate("");
    setToDate("");
    setAuthorCode("");
    setIsShowFilterDate(false);
    setEndDate(null);
    setStartDate(null);
    setPageNumber(1);
    setPageSize(12);
    setsortBy("Date");
    setDocumentTags("");
    setIsKeyword(false);

    const urlSortBy = "";
    const urlPageSize = 12;
    const urlPageNumber = 0;
    const urlKeyword = "";
    const urlCategoryID = 0;
    const urlAuthorCode = "";
    const urlFromDate = "";
    const urlToDate = "";
    const urlDocumentTags = "";

    setFilterValues(() => ({
      urlSortBy,
      urlPageSize,
      urlPageNumber,
      urlKeyword,
      urlCategoryID,
      urlAuthorCode,
      urlFromDate,
      urlToDate,
      urlDocumentTags,
    }));
    updateUrl(
      urlSortBy,
      urlPageSize,
      urlPageNumber,
      urlKeyword,
      urlCategoryID,
      urlAuthorCode,
      urlFromDate,
      urlToDate
    );
    navigate(`/news-and-articles/news-articles`);
    NewsArticlesSearcType = {
      pageSize: urlPageSize,
      sortBy: "Date",
      pageNumber: 1,
      keyword: "",
      categoryID: urlCategoryID,
      authorCode: urlAuthorCode,
      fromDate: urlFromDate,
      toDate: urlToDate,
      documentTags: urlDocumentTags,
    };
    dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
  };

  useEffect(() => {
    if (!isInitialPageLoad) {
      updateUrl(
        filterValues.urlSortBy,
        filterValues.urlPageSize,
        filterValues.urlPageNumber,
        filterValues.urlKeyword,
        filterValues.urlCategoryID,
        filterValues.urlAuthorCode,
        filterValues.urlFromDate,
        filterValues.urlToDate,
        filterValues.urlDocumentTags
      );
    } else {
      setIsInitialPageLoad(false);
    }
  }, [filterValues]);

  const handlePageChange = (page: number) => {
    const urlPageNumber = page;
    setPageNumber(urlPageNumber);
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlPageNumber,
    }));
  };

  const handlePageSize = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const urlPageSize = parseInt(event.target.value);
    setPageSize(urlPageSize as pageSizeType);
    setPageNumber(1);
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlPageSize,
    }));
  };

  const handleSortBy = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const urlSortBy = event.target.value;
    setsortBy(urlSortBy as sortByType);
    setFilterValues((prevFilterValues) => ({ ...prevFilterValues, urlSortBy }));
  };

  // Keyword
  const handleKeyword = (value: string) => {
    if (value.trim() !== "" || value) {
      const urlKeyword = value;
      setKeyword(value);
      setIsKeyword(true);
      setFilterValues((prevFilterValues) => ({
        ...prevFilterValues,
        urlKeyword,
      }));
      dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
    }else{
      NewsArticlesSearcType = {
        pageSize: pageSize,
        sortBy: sortBy,
        pageNumber: pageNumber,
        keyword: '',
        categoryID: categoryID,
        authorCode: authorCode,
        fromDate: fromDate,
        toDate: toDate,
        documentTags: documentTags,
      };
    
      dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
      const urlKeyword = '';
        setFilterValues((prevFilterValues) => ({
          ...prevFilterValues,
          urlKeyword,
        }));
    }
  };

  // Remove the Keyword filter
  const removeKeywordItem = () => {
    setKeyword("");
    const urlKeyword = "";
    setIsKeyword(false);
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlKeyword,
    }));
    NewsArticlesSearcType = {
      pageSize: pageSize,
      sortBy: sortBy,
      pageNumber: pageNumber,
      keyword: "",
      categoryID: categoryID,
      authorCode: authorCode,
      fromDate: fromDate,
      toDate: toDate,
      documentTags: documentTags,
    };
    dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
  };

  // On change Category
  const handleCategory = (value: number) => {
    setCategoryID(value);
    const urlCategoryID = value;
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlCategoryID,
    }));
  };

  // Remove the Keyword filter
  const removeCategoryItem = () => {
    setCategoryID(0);
    const urlCategoryID = 0;
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlCategoryID,
    }));
  };

  // On change Author
  const handleAuthor = (value: string) => {
    setAuthorCode(value);
    const urlAuthorCode = value;
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlAuthorCode,
    }));
  };

  // Remove the Keyword filter
  const removeAuthorItem = () => {
    setAuthorCode("");
    const urlAuthorCode = "";
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlAuthorCode,
    }));
  };

  //const [myRef, setMyRef] = useState(false);

  // On Change From Date
  const handleFromDate = (date: Date) => {
    if (date) {
      let curFromDate = new Date(date);
      let getFromDate = curFromDate.toLocaleDateString();
      setFromDate(getFromDate);
      const urlFromDate = formatDate(date);
      setFilterValues((prevFilterValues) => ({
        ...prevFilterValues,
        urlFromDate,
      }));
    }
  };

  const handleToDate = (date: Date) => {
    if (date) {
      let curToDate = new Date(date);
      let getToDate = curToDate.toLocaleDateString();
      setToDate(getToDate);
      const urlToDate = formatDate(date);
      setFilterValues((prevFilterValues) => ({
        ...prevFilterValues,
        urlToDate,
      }));
    }
  };

  // Remove the Keyword filter
  const removeFromDateItem = () => {
    setFromDate("");
    setStartDate(null);
    const urlFromDate = "";
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      urlFromDate,
    }));
  };

  // Remove the Keyword filter
  const removeToDateItem = () => {
    setToDate("");
    setEndDate(null);
    const urlToDate = "";
    setFilterValues((prevFilterValues) => ({ ...prevFilterValues, urlToDate }));
  };

  const totalRecords = NewsArticlesSearcData[0]?.totalSearchResults;

  const totalPages = Math.ceil(totalRecords / pageSize);

  useEffect(() => {
    dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
  }, [pageNumber, sortBy, pageSize, categoryID, authorCode, fromDate, toDate, documentTags]);

  // On click Search
  const handleSearch = () => {

      if (keyword.trim() !== "") {
          const urlKeyword = keyword;
          setIsKeyword(true);
          setFilterValues((prevFilterValues) => ({
              ...prevFilterValues,
              urlKeyword,
          }));
          dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
      }
    if (
      keyword.trim() !== "" ||
      keyword ||
      isKeyword ||
      categoryID !== 0 ||
      authorCode ||
      fromDate.trim() !== "" ||
      fromDate ||
      toDate.trim() !== "" ||
      toDate
    ) {
      dispatch(retrieveNewsArticlesSearch(NewsArticlesSearcType));
      // setIsShowFilterDate(true);
    }
  };

  // const handleNarrowResults
  const handleNarrowResults = () => {
    setIsShowFilter(!isShowFilter);
  };

  // Handle End today date
  const handleEndTodayDate = () => {
    let curToDate = new Date();
    setEndDate(curToDate);

    const urlToDate = formatDate(curToDate);
    setFilterValues((prevFilterValues) => ({ ...prevFilterValues, urlToDate }));
    updateUrl(
      filterValues.urlSortBy,
      filterValues.urlPageSize,
      filterValues.urlPageNumber,
      filterValues.urlKeyword,
      filterValues.urlCategoryID,
      filterValues.urlAuthorCode,
      filterValues.urlFromDate,
      urlToDate,
      filterValues.urlDocumentTags
    );
  };

  const cachedLoadGoogleAds = useMemo(
    () => <LoadNewsAndArticlesListingAds />,
    []
  );

  const [isArticlesListed, setIsArticlesListed] = useState(false);

  useEffect(() => {
    if (
      NewsArticlesSearcData[0]?.newArticlesSearchItemList.length > 0 &&
      !isArticlesListed
    ) {
      setIsArticlesListed(true);
    }
  }, [NewsArticlesSearcData]);

  return (
    <>
      <Helmet>
        <title>
          {NewsArticlesSearcData[0]?.breadCrumb[1]?.nodeName ===
            "/News-and-Articles/News-Articles" &&
            NewsArticlesSearcData[0]?.breadCrumb[1]?.menuItemName}
        </title>
      </Helmet>
      <div className="site-top">
        <div
          className="top-banner"
          id="topbanner"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="CMSBanner Banner topbanner gadsbanner"
            id="div-gpt-ad-1611326459208-0"
            style={{
              width: "970px",
              height: "90px",
              display: "none",
              margin: "0 auto",
            }}
          ></div>
          <div
            className="CMSBanner Banner topmobile gadsbanner"
            id="div-gpt-ad-1611693088874-0"
            style={{
              width: "300px",
              height: "50px",
              display: "none",
              margin: "0 auto",
            }}
          ></div>
        </div>
        <style type="text/css">{`
        @media only screen and (max-width: 991px) {
          .topmobile, .contentmobile {
            display: block;
          }
          .topbanner, .contentbanner {
            display: none;
          }
        }
        @media only screen and (min-width: 991px) {
          .topmobile, .contentmobile {
            display: none;
          }
          .topbanner, .contentbanner {
            display: block;
          }
        }
      `}</style>
      </div>
      <div className="container">
        <div className="page-tools row">
          <div className="col-sm-8 col-lg-9">
            <div className="breadcrumb">
              {/* <Breadcrumb></Breadcrumb> */}
              <div className="breadcrumb">
                <a href="/news-and-articles" className="CMSBreadCrumbsLink">
                  Regulatory Focus™
                </a>{" "}
                &gt;{" "}
                <span className="CMSBreadCrumbsCurrentItem">News Articles</span>
              </div>
            </div>
          </div>
          <div className="col-sm-4 col-lg-3"></div>
        </div>
      </div>
      <div className="container two-column-alt">
        {/* <hr />
                <h1>News Article More</h1>
                <hr /> */}
        <div className="row">
          <div className="col-lg-3">
            <div className="filter filter-rail fitler-event-search">
              <header className="filter-header">
                <h3>Narrow Results</h3>
                <button
                  id="data-toggle-4" type="button" className="filter-toggle" data-toggle=""
                  data-target="toggledContent_EventFilterRail"
                  data-init="" aria-haspopup="true" aria-expanded="false"
                  onClick={() => handleNarrowResults()}>
                  {isShowFilter ? "-" : "+"}
                </button>
              </header>
              <Collapse in={isShowFilter}>
                <div id="toggledContent_EventFilterRail"
                  className="form-container" aria-labelledby="data-toggle-4">
                  <input
                    type="hidden"
                    name="p$lt$ctl04$SiteMainPlaceholder$p$lt$ctl03$NewsSmartSearchFilter$hdnDocumentTag"
                    id="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_hdnDocumentTag"
                  />
                  {isShowFilterDate ||
                  (isKeyword && keyword.trim() !== '') ||
                  categoryID ||
                  authorCode ||
                  fromDate ||
                  toDate ? (
                    <div id="sort-details" className="current-facets">
                      <p className="facet-heading">You've Selected</p>

                      <ul className="list-unstyled list-facet-active">
                        {isKeyword ? (
                          <li>
                            <span
                              id="close-Keyword"
                              className="facet-clear"
                              onClick={() => removeKeywordItem()}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </span>
                            Keyword
                            <ul className="list-inline list-facet-items">
                              <li>
                                <button
                                  disabled={true}
                                  className="btn btn-default btn-sm"
                                >
                                  {keyword}
                                </button>
                              </li>
                            </ul>
                          </li>
                        ) : null}

                        {categoryID ? (
                          <li className="">
                            <span
                              id="close-category"
                              className="facet-clear"
                              onClick={() => removeCategoryItem()}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </span>
                            Category
                            <ul className="list-inline list-facet-items">
                              <li>
                                {NewsArticlesSearchCategoryData[0]?.categoryInfos?.map(
                                  (
                                    item: {
                                      categoryId: number;
                                      categoryName:
                                        | string
                                        | number
                                        | boolean
                                        | React.ReactElement<
                                            any,
                                            | string
                                            | React.JSXElementConstructor<any>
                                          >
                                        | React.ReactFragment
                                        | React.ReactPortal
                                        | null
                                        | undefined;
                                    },
                                    index: React.Key | null | undefined
                                  ) => {
                                    return item.categoryId === categoryID ? (
                                      <button
                                        key={index}
                                        disabled={true}
                                        type="button"
                                        className="btn btn-default btn-sm"
                                      >
                                        {item.categoryName}{" "}
                                      </button>
                                    ) : null;
                                  }
                                )}
                              </li>
                            </ul>
                          </li>
                        ) : null}

                        {fromDate ? (
                          <li className="">
                            <span
                              id="close-from-date"
                              className="facet-clear"
                              onClick={() => removeFromDateItem()}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </span>
                            From Date
                            <ul className="list-inline list-facet-items">
                              <li>
                                <button
                                  disabled={true}
                                  className="btn btn-default btn-sm"
                                >
                                  {fromDate}
                                </button>
                              </li>
                            </ul>
                          </li>
                        ) : null}

                        {toDate ? (
                          <li className="">
                            <span
                              id="close-from-date"
                              className="facet-clear"
                              onClick={() => removeToDateItem()}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </span>
                            To Date
                            <ul className="list-inline list-facet-items">
                              <li>
                                <button
                                  disabled={true}
                                  className="btn btn-default btn-sm"
                                >
                                  {toDate}
                                </button>
                              </li>
                            </ul>
                          </li>
                        ) : null}

                        {authorCode ? (
                          <li className="">
                            <span
                              id="close-author"
                              className="facet-clear"
                              onClick={() => removeAuthorItem()}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </span>
                            Author
                            <ul className="list-inline list-facet-items">
                              <li>
                                {NewsArticlesSearchAuthorData[0]?.authors?.map(
                                  (item, index) => {
                                    return item.authorsCode === authorCode ? (
                                      <button
                                        key={index}
                                        disabled={true}
                                        type="button"
                                        className="btn btn-default btn-sm"
                                      >
                                        {item.authorName}{" "}
                                      </button>
                                    ) : null;
                                  }
                                )}
                              </li>
                            </ul>
                          </li>
                        ) : null}
                      </ul>

                      <div className="text-right">
                        <span
                          id="clear-sort"
                          className="facet-clear-all"
                          onClick={() => clearSort()}
                        >
                          Clear All
                        </span>
                      </div>
                    </div>
                  ) : null}

                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSearch();
                    }}
                  >
                    <fieldset>
                      <div className="form-group">
                        <div className="input-group-sm">
                          <label
                            htmlFor="keyword"
                            id="keyword-label"
                            className="EditingFormLabel"
                          >
                            Keyword
                          </label>
                          <input
                            name="keyword"
                            type="text"
                            id="keyword"
                            value={keyword}
                            className="form-control"
                            onChange={(e) => {
                              setKeyword(e.target.value);
                              if((e.target.value).trim() === ''){
                              setIsKeyword(false)
                              const urlKeyword = '';
                              setFilterValues((prevFilterValues) => ({
                                ...prevFilterValues,
                                urlKeyword,
                              }));
                            }}
                          }
                          />
                        </div>
                      </div>

                      {/* <div className="form-group">
                      <div className="input-group-sm">
                        <label
                          htmlFor="date-range"
                          id="date-range"
                          className="EditingFormLabel"
                        >
                          Select Date Range
                        </label>
                        
                        <DateRangePicker
                          editable={true}
                          format="MM-dd-yyyy"
                        //   value={[new Date(fromDate), new Date(toDate)]}
                          placeholder="Select Date Range"
                          defaultCalendarValue={[addMonths(today, -1), today]}
                          disabledDate={(date) => isAfter(date, new Date())}
                          onChange={(e) => {
                            handleRangeDate(e);
                          }}
                        />
                      </div>
                    </div> */}

                      <div className="form-group">
                        <div className="input-group-sm">
                          <label
                            htmlFor="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_dtFromDate"
                            id="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_lblFromDate"
                            className="EditingFormLabel"
                          >
                            From Date
                          </label>

                          <DatePicker
                            selected={startDate}
                            onChange={(
                              date: React.SetStateAction<Date | null>
                            ) => {
                              date && setStartDate(date);
                              handleFromDate(date as Date);
                            }}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            maxDate={addDays(new Date(), 0)}
                            placeholderText="Select Date"
                            dropdownMode="select"
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            todayButton="Today"
                          >
                            {/* <div className="calander-btns">
                            <input type="submit" className="btn btn-default btn-block" value="TODAY" onClick={handleStartTodayDate} />
                            <input type="submit" className="btn btn-secondary btn-block btn-primary" value="SELECT" />
                          </div> */}
                          </DatePicker>
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group-sm">
                          <label
                            htmlFor="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_dtToDate"
                            id="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_lblToDate"
                            className="EditingFormLabel"
                          >
                            To Date
                          </label>

                          <DatePicker
                            selected={endDate}
                            onChange={(
                              date: React.SetStateAction<Date | null>
                            ) => {
                              date && setEndDate(date);
                              handleToDate(date as Date);
                            }}
                            selectsEnd
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            maxDate={addDays(new Date(), 0)}
                            placeholderText="Select Date"
                            dropdownMode="select"
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            todayButton="Today"
                          >
                            {/* <div className="calander-btns">
                            <input type="submit" className="btn btn-default btn-block" value="TODAY" onClick={handleEndTodayDate} />
                            <input type="submit" className="btn btn-secondary btn-block btn-primary" value="SELECT" />
                          </div> */}
                          </DatePicker>
                        </div>
                      </div>

                      <div className="form-group">
                        <div className="input-group-sm">
                          <label
                            htmlFor="categories"
                            className="EditingFormLabel"
                          >
                            Category
                          </label>
                          <select
                            name="categories"
                            id="categories"
                            className="form-control"
                            value={categoryID}
                            onChange={(e) =>
                              handleCategory(Number(e.target.value))
                            }
                          >
                            <option value="0">Select Categories</option>
                            {NewsArticlesSearchCategoryData &&
                              NewsArticlesSearchCategoryData[0]?.categoryInfos?.map(
                                (
                                  item: {
                                    categoryId:
                                      | string
                                      | number
                                      | readonly string[]
                                      | undefined;
                                    categoryName:
                                      | string
                                      | number
                                      | boolean
                                      | React.ReactElement<
                                          any,
                                          | string
                                          | React.JSXElementConstructor<any>
                                        >
                                      | React.ReactFragment
                                      | React.ReactPortal
                                      | null
                                      | undefined;
                                  },
                                  index: React.Key | null | undefined
                                ) => {
                                  return (
                                    <option key={index} value={item.categoryId}>
                                      {item.categoryName}
                                    </option>
                                  );
                                }
                              )}
                          </select>
                        </div>
                      </div>
                      <div className="form-group" style={{ display: "none" }}>
                        <div className="input-group-sm">
                          <label
                            htmlFor="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_drpTopic"
                            className="EditingFormLabel"
                          >
                            Topic
                          </label>
                          <select
                            name="p$lt$ctl04$SiteMainPlaceholder$p$lt$ctl03$NewsSmartSearchFilter$drpTopic"
                            id="p_lt_ctl04_SiteMainPlaceholder_p_lt_ctl03_NewsSmartSearchFilter_drpTopic"
                            className="form-control"
                          >
                            <option value="Select Topic">Select Topic</option>
                            <option value="Biotech">Biotech</option>
                            <option value="Careers">Careers</option>
                            <option value="Combination Products">
                              Combination Products
                            </option>
                            <option value="EU MDR">EU MDR</option>
                            <option value="IVDs">IVDs</option>
                            <option value="Management">Management</option>
                            <option value="Medical Devices">
                              Medical Devices
                            </option>
                            <option value="Pharmaceuticals">
                              Pharmaceuticals
                            </option>
                            <option value="Regulatory Intelligence">
                              Regulatory Intelligence
                            </option>
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group-sm">
                          <label htmlFor="author" className="EditingFormLabel">
                            Author
                          </label>
                          <select
                            name="author"
                            id="author"
                            className="form-control"
                            value={authorCode}
                            onChange={(e) => handleAuthor(e.target.value)}
                          >
                            <option value="0">Select Author</option>
                            {NewsArticlesSearchAuthorData &&
                              NewsArticlesSearchAuthorData[0]?.authors?.map(
                                (item, index) => {
                                  return (
                                    <option
                                      key={index}
                                      value={item.authorsCode}
                                    >
                                      {item.authorName}
                                    </option>
                                  );
                                }
                              )}
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <div id="pnlButtons" className="row g-3">
                          <div className="col-md-6">
                            <input
                              type="submit"
                              name="search"
                              value="Search"
                              id="btnSearch"
                              onClick={() => {
                                handleSearch();
                              }}
                              className="btn btn-default btn-block btn-responsive"
                              />
                          </div>
                          <div className="col-md-6">
                            <input
                              type="reset"
                              name="reset"
                              value="Clear"
                              id="btnReset"
                              className="btn btn-secondary btn-block btn-primary btn-responsive"
                              onClick={() => clearSort()}
                              />
                          </div>
                        </div>
                      </div>
                    </fieldset>
                  </form>
                </div>
              </Collapse>
            </div>
          </div>
          <div className="col-lg-9">
            <div className="news module listing news-articles-list">
              <ul className="item-list">
                <div>
                  {totalRecords !== 0 ? (
                    <div className="sort-status">
                      <div className="sort-results">
                        <label>
                          Results{" "}
                          <strong>
                            {pageNumber * pageSize - pageSize + 1}-
                            {pageSize * pageNumber > totalRecords
                              ? totalRecords
                              : pageSize * pageNumber}
                          </strong>{" "}
                          of <strong> {totalRecords}</strong>
                        </label>
                      </div>
                      <div className="sort-count">
                        <div className="form-inline">
                          <div className="input-group-sm">
                            <label
                              className="form-label"
                              htmlFor="sortShowCount"
                            >
                              Show
                            </label>
                            {/* onChange={(e) => handlePageSize(e.target.value)} */}
                            <select
                              id="pageSize"
                              className="form-control"
                              value={pageSize}
                              onChange={handlePageSize}
                            >
                              <option value="12">12 per page</option>
                              <option value="24">24 per page</option>
                              <option value="48">48 per page</option>
                              <option value="96">96 per page</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="sort-by">
                        <div className="form-inline">
                          <div className="input-group-sm">
                            <label
                              className="form-label"
                              htmlFor="sortShowCount"
                            >
                              Sort By
                            </label>
                            <select
                              id="sortShowCount"
                              className="form-control"
                              value={sortBy}
                              onChange={handleSortBy}
                            >
                              <option value="Name">Name</option>
                              <option value="Date">Date</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="sort-paging">
                        {totalPages > 1 && (
                          <Pagination
                            className="pagination pagination-sm"
                            {...bootstrap5PaginationPreset} // include Bootstrap 5 preset
                            total={totalPages}
                            current={pageNumber}
                            onPageChange={(page: number) =>
                              handlePageChange(page)
                            }
                          />
                        )}
                      </div>
                    </div>
                  ) : null}

                  {NewsArticlesSearcLoading ? (
                    <LoadingComp />
                  ) : totalRecords === 0 ? (
                    <div className="no-result">
                      <span>No results were found.</span>
                    </div>
                  ) : (
                    NewsArticlesSearcData &&
                    NewsArticlesSearcData[0]?.newArticlesSearchItemList?.map(
                      (item, index) => {
                        return (
                          <React.Fragment key={index}>
                            {index === 4 && (
                              <div
                                className="col-md-12"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginBottom: 40,
                                }}
                                key={"ads1" + index}
                              >
                                <div
                                  className="CMSBanner Banner topbanner gadsbanner"
                                  id="div-gpt-ad-1649701637206-0"
                                  style={{
                                    minWidth: "970px",
                                    minHeight: "90px",
                                    display: "none",
                                  }}
                                ></div>
                                <div
                                  className="CMSBanner Banner topmobile gadsbanner"
                                  id="div-gpt-ad-1649701809430-0"
                                  style={{
                                    minWidth: "300px",
                                    minHeight: "50px",
                                    display: "none",
                                  }}
                                ></div>
                              </div>
                            )}
                            {index === 8 && (
                              <div
                                className="col-md-12"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginBottom: 40,
                                }}
                                key={"ads2" + index}
                              >
                                <div
                                  className="CMSBanner Banner topbanner gadsbanner"
                                  id="div-gpt-ad-1649701921828-0"
                                  style={{
                                    minWidth: "970px",
                                    minHeight: "90px",
                                    display: "none",
                                  }}
                                ></div>
                                <div
                                  className="CMSBanner Banner topmobile gadsbanner"
                                  id="div-gpt-ad-1649701861184-0"
                                  style={{
                                    minWidth: "300px",
                                    minHeight: "50px",
                                    display: "none",
                                  }}
                                ></div>
                              </div>
                            )}
                            <li className="item" key={index}>
                              <figure className="thumbnail">
                                <img style={{ width: "900px" }} alt={item.title} src={encodeURI(item.imageUrl).replace(/%25/g, "%")} /> {/*remove encoding of %*/}
                              </figure>
                              <div className="item-content">
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: item.badgeUrl,
                                  }}
                                ></span>
                                <h4 className="title">
                                  <Link
                                    className="members-only"
                                    to={item.nodeName.toLowerCase()}
                                  >
                                    {item.title}
                                  </Link>
                                </h4>
                                <ul className="meta">
                                  <li className="meta-item date">
                                    {item.formattedDate}
                                  </li>
                                  <li className="meta-item author">
                                    {" "}
                                    {item.author ? (
                                      <>
                                        By{" "}
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: item.author,
                                          }}
                                        ></span>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </li>
                                </ul>
                                <div
                                  className="summary"
                                  dangerouslySetInnerHTML={{
                                    __html: item.contents,
                                  }}
                                ></div>
                              </div>
                            </li>
                          </React.Fragment>
                        );
                      }
                    )
                  )}

                  {totalPages > 1 && (
                    <div className="sort-status-bottom">
                      <div className="sort-paging">
                        <Pagination
                          className="pagination pagination-sm"
                          {...bootstrap5PaginationPreset} // include Bootstrap 5 preset
                          total={totalPages}
                          current={pageNumber}
                          onPageChange={(page: number) =>
                            handlePageChange(page)
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <p>&nbsp;</p>
      <div className="container">
        <div className="col-md-12">
          <div className="row img-width">
            <div
              className="col-md-12"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                className="CMSBanner Banner topbanner gadsbanner"
                id="div-gpt-ad-1649702008484-0"
                style={{
                  minWidth: "970px",
                  minHeight: "90px",
                  display: "none",
                }}
              ></div>
              <div
                className="CMSBanner Banner topmobile gadsbanner"
                id="div-gpt-ad-1649702035816-0"
                style={{
                  minWidth: "300px",
                  minHeight: "50px",
                  display: "none",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      {isArticlesListed && cachedLoadGoogleAds}
    </>
  );
};

export default NewsAndArticleListComponent;
